<template>
  <transition name="fade">
    <div v-if="showDialog" class="dialog-overlay" @click="closeDialog">
      <div class="dialog" @click.stop>
        <header class="dialog-header">
          <h3>Review Content</h3>
          <button class="close-button" @click="closeDialog">
            <i class="fas fa-times"></i>
          </button>
        </header>
        <div class="dialog-body">
          <div class="card form-group">
            <label for="title" class="form-label">Title:</label>
            <p id="title">{{ reviewContentData.title }}</p>
          </div>

          <div class="card form-group">
            <label for="description" class="form-label">Description:</label>
            <p id="description">{{ reviewContentData.description }}</p>
          </div>
          <div class="card form-group">
            <label for="fileTitle" class="form-label">File Title:</label>
            <p id="fileTitle">{{ reviewContentData.fileTitle }}</p>
          </div>
          <div class="card form-group">
            <label for="fileLink" class="form-label">File Link:</label>
            <p id="fileLink">{{ reviewContentData.fileLink }}</p>
          </div>
          <div class="card form-group" style="display: flex">
            <label for="type" class="form-label">Type:</label>
            <p id="type">
              <i
                v-if="reviewContentData.type === 'banner'"
                class="fas fa-image"
              ></i>
              <i
                v-if="reviewContentData.type === 'video'"
                class="fas fa-video"
              ></i>
              {{ reviewContentData.type }}
            </p>
          </div>
          <div
            v-if="reviewContentData.type === 'banner'"
            class="card form-group"
          >
            <label for="banner" class="form-label">Banner:</label>
            <img
              :src="reviewContentData.banner"
              alt="Banner Image"
              id="banner"
              class="form-control !w-full !h-full !object-full"
            />
          </div>
          <div
            v-if="reviewContentData.type === 'video'"
            class="card form-group"
          >
            <label for="videoLink" class="form-label">Video Link:</label>
            <iframe
              v-if="getEmbedUrl(reviewContentData.videoLink)"
              :src="getEmbedUrl(reviewContentData.videoLink)"
              width="100%"
              height="200"
              frameborder="0"
              allowfullscreen
              class="rounded-t-xl"
            ></iframe>
          </div>
          <div class="card form-group">
            <label for="content" class="form-label">Content:</label>
          </div>
          <div class="w-full" v-html="reviewContentData.content"></div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script setup>
import { ref, defineProps, defineEmits, onMounted, watch } from "vue";

const props = defineProps(["openDialog", "reviewContentData"]);
const emit = defineEmits(["close"]);

const showDialog = ref(false);

const openDialog = () => {
  showDialog.value = true;
};

const closeDialog = () => {
  showDialog.value = false;
  emit("close");
};

const getEmbedUrl = (url) => {
  let videoId;
  if (url.includes("youtube.com")) {
    videoId = url.split("v=")[1];
    const ampersandPosition = videoId ? videoId.indexOf("&") : -1;
    if (ampersandPosition !== -1) {
      videoId = videoId.substring(0, ampersandPosition);
    }
  } else if (url.includes("youtu.be")) {
    videoId = url.split("/").pop();
  } else {
    return null; // Return null for non-YouTube URLs
  }
  return `https://www.youtube.com/embed/${videoId}`;
};

onMounted(() => {
  if (props.openDialog) {
    openDialog();
  }
});

watch(
  () => props.openDialog,
  (newVal) => {
    if (newVal) {
      openDialog();
    } else {
      closeDialog();
    }
  },
);
</script>

<style scoped>
@import url("https://cdnjs.cloudflare.com/ajax/libs/font-awesome/5.15.4/css/all.min.css");

.dialog-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 50;
}

.dialog {
  background: white;
  padding: 20px;
  border-radius: 0; /* Remove border-radius for full screen */
  width: 100%;
  height: 100%; /* Make dialog full height */
  max-width: 100%; /* Remove max-width constraint */
  max-height: 100%; /* Remove max-height constraint */
  box-shadow: none; /* Remove box-shadow */
  position: relative;
  animation: slide-down 0.3s ease-out;
  display: flex;
  flex-direction: column; /* Ensure content is vertically aligned */
}

.dialog-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #ddd;
  padding-bottom: 10px;
  margin-bottom: 20px;
}

.dialog-body {
  flex: 1; /* Allow body to take up remaining space */
  overflow-y: auto;
}

.close-button {
  background: none;
  border: none;
  font-size: 16px;
  cursor: pointer;
}

.close-button i {
  font-size: 20px;
  color: #333;
}

.card {
  background: #f9f9f9;
  border-radius: 8px;
  padding: 15px;
  margin-bottom: 15px;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
}

.form-label {
  font-weight: bold;
  margin-bottom: 5px;
  display: block;
}

.form-control {
  width: 100%;
  padding: 8px;
  box-sizing: border-box;
  border: 1px solid #ccc;
  border-radius: 4px;
}

.form-group.has-error .form-control {
  border-color: red;
}

.error-text {
  color: red;
  font-size: 12px;
  margin-top: 5px;
}

.create-button {
  background-color: #007bff;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}

.create-button:disabled {
  background-color: #cccccc;
  cursor: not-allowed;
}

.spinner {
  border: 2px solid #f3f3f3;
  border-top: 2px solid #007bff;
  border-radius: 50%;
  width: 16px;
  height: 16px;
  animation: spin 1s linear infinite;
}

@keyframes slide-down {
  from {
    transform: translateY(-20px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}
</style>
